import type { ResetAskedDto } from '@nsb/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { api, ApiError, errorText, queryClient } from '../api.ts';
import { BrandLockup } from '../components/Brand.tsx';
import { Threshold } from '../components/Threshold.tsx';
import { Button, ErrorLine, Field, TextInput } from '../components/ui.tsx';
import { PublicLinks } from './Public.tsx';
import { ResetSent } from './ResetPassword.tsx';

/** Running under Vite on this machine. The API decides whether the local sign-in exists at all (a 404 anywhere else). */
const DEV = import.meta.env.DEV;

interface DevSignIn {
  email: string;
  name: string;
  password: string;
}

/** The API restarting (code changed under `npm run dev`) answers through the proxy as a 5xx or not at all. */
const restarting = (e: unknown) => !(e instanceof ApiError) || e.status >= 500;

/** Locally, keep trying for a while when the API is restarting, instead of failing the sign-in. */
async function patiently<T>(fn: () => Promise<T>, onWait: () => void): Promise<T> {
  const until = Date.now() + 25_000;
  for (;;) {
    try {
      return await fn();
    } catch (e) {
      if (!DEV || !restarting(e) || Date.now() > until) {
        if (DEV && restarting(e)) throw new ApiError(503, 'api_down', 'The API is not answering. Check the terminal running npm run dev.', null);
        throw e;
      }
      onWait();
      await new Promise((r) => setTimeout(r, 1000));
    }
  }
}

export function Login() {
  const [params] = useSearchParams();
  // Only a path in the portal: never another site, nor a protocol-relative "//elsewhere".
  const asked = params.get('next');
  const next = asked && /^\/(?![/\\])/.test(asked) ? asked : undefined;
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [usePassword, setUsePassword] = useState(DEV || params.get('with') === 'password');
  // Forgot your password (Jason, 2026-10-08): an address no account uses is told so; a known one is emailed a link to
  // a page that asks for the new password (ResetPassword.tsx). `?forgot=1` opens on it (a reset link that did not work).
  const [forgot, setForgot] = useState(params.get('forgot') === '1');
  const [sent, setSent] = useState<'link' | { reset: ResetAskedDto; to: string } | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Locally: who the one-click sign-in is, asked again while the API restarts.
  const { data: dev } = useQuery({
    queryKey: ['dev-sign-in'],
    queryFn: () => api<DevSignIn>('/dev/sign-in'),
    enabled: DEV,
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 30,
    retryDelay: 1000,
    staleTime: Infinity,
  });
  // The form starts filled in with the local sign-in too, so Enter always works.
  const prefilled = useRef(false);
  useEffect(() => {
    if (!dev || prefilled.current) return;
    prefilled.current = true;
    setEmail((v) => v || dev.email);
    setPassword((v) => v || dev.password);
    setUsePassword(true);
  }, [dev]);

  const signedIn = async () => {
    await queryClient.invalidateQueries({ queryKey: ['me'] });
    navigate(next ?? '/', { replace: true });
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await patiently(fn, () => setWaiting(true));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
      setWaiting(false);
    }
  };

  const devSignIn = () =>
    run(async () => {
      await api('/dev/sign-in', { body: {} });
      await signedIn();
    });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    await run(async () => {
      if (forgot) {
        const reset = await api<ResetAskedDto>('/auth/reset', { body: { email, redirect: next } });
        setSent({ reset, to: email.trim().toLowerCase() });
      } else if (usePassword) {
        await api('/auth/password', { body: { email, password } });
        await signedIn();
      } else {
        const r = await api<{ ok: true; devLink?: string }>('/auth/magic-link', { body: { email, redirect: next } });
        setSent('link');
        setDevLink(r.devLink ?? null);
      }
    });
  };

  return (
    <Threshold>
      <div className="mb-14" role="img" aria-label="Never Stop Building, build portal">
        <BrandLockup />
      </div>
      <h1 className="text-[2.5rem]">{forgot ? 'Forgot your password?' : 'Sign in'}</h1>
      <p className="mb-8 mt-2 text-ink-2">{forgot ? 'We will email you a link to choose a new one.' : 'To your project portal.'}</p>
      {dev && !sent ? (
        <div className="mb-10 border-y border-rule py-5">
          <div className="label mb-3">On this machine</div>
          <Button busy={busy} onClick={() => void devSignIn()}>
            Sign in as {dev.name}
          </Button>
          <p className="meta mt-3">
            {dev.email} · {dev.password}
          </p>
          {waiting ? <p className="mt-2 text-sm text-ink-2">Waiting for the API to finish restarting…</p> : null}
        </div>
      ) : null}
      {sent ? (
        <div>
          {sent === 'link' ? (
            <>
              <p className="text-lg">Check your email for a sign-in link.</p>
              <p className="meta mt-2">It works once, for twenty minutes. You can close this tab.</p>
              {devLink ? (
                <p className="mt-6 text-sm text-ink-3">
                  Development: <a className="link" href={devLink}>open the link</a>
                </p>
              ) : null}
            </>
          ) : (
            <ResetSent sent={sent.reset} to={sent.to} />
          )}
          <div className="mt-8 flex flex-wrap items-center gap-x-6">
            {/* A different address keeps to what was being done: a reset asks again, a sign-in link signs in. */}
            <Button tone="quiet" onClick={() => setSent(null)}>
              Use a different email
            </Button>
            {sent === 'link' ? null : (
              <Button
                tone="quiet"
                onClick={() => {
                  setSent(null);
                  setForgot(false);
                }}
              >
                Back to signing in
              </Button>
            )}
          </div>
        </div>
      ) : (
        <form onSubmit={submit}>
          <Field label="Email">
            {(id) => <TextInput id={id} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />}
          </Field>
          {forgot ? null : usePassword ? (
            <Field label="Password">
              {(id) => (
                <TextInput id={id} type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
              )}
            </Field>
          ) : null}
          <ErrorLine error={error} />
          <div className="mt-8 flex flex-wrap items-center gap-x-6">
            <Button type="submit" busy={busy}>
              {forgot ? 'Email me the link' : usePassword ? 'Sign in' : 'Email me a link'}
            </Button>
            {forgot ? (
              <Button
                tone="quiet"
                onClick={() => {
                  setForgot(false);
                  setError(null);
                }}
              >
                Back to signing in
              </Button>
            ) : (
              <Button tone="quiet" onClick={() => setUsePassword((v) => !v)}>
                {usePassword ? 'Use a link instead' : 'Use a password'}
              </Button>
            )}
            {usePassword && !forgot ? (
              <Button
                tone="quiet"
                onClick={() => {
                  setForgot(true);
                  setError(null);
                }}
              >
                Forgot your password?
              </Button>
            ) : null}
          </div>
        </form>
      )}
      <PublicLinks className="mt-14" />
    </Threshold>
  );
}

/** The link lands here; the token is in the fragment, so it never reaches a server log. */
export function Verify() {
  const navigate = useNavigate();
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('t'));
  const [failure, setError] = useState<string | null>(null);
  // A client's button that has run out offers a new one; a client's button opened by someone in NSB says to sign in.
  const [code, setCode] = useState<string | null>(null);
  const [renewed, setRenewed] = useState<{ devLink?: string } | null>(null);
  const [renewing, setRenewing] = useState(false);
  const error = token ? failure : 'This link is incomplete. Ask for a new one.';
  const once = useRef(false);
  const renew = async () => {
    setRenewing(true);
    try {
      setRenewed(await api<{ devLink?: string }>('/auth/renew', { body: { token } }));
    } catch (e) {
      setError(errorText(e));
      setCode(null);
    } finally {
      setRenewing(false);
    }
  };
  useEffect(() => {
    if (once.current || !token) return;
    once.current = true;
    // Take the token out of the address bar and history before anything else.
    window.history.replaceState(null, '', window.location.pathname);
    api<{ redirect: string }>('/auth/verify', { body: { token } })
      .then(async (r) => {
        await queryClient.invalidateQueries({ queryKey: ['me'] });
        navigate(r.redirect || '/', { replace: true });
      })
      .catch((e) => {
        // A link to choose a new password, emailed before it had a page of its own: on to that page, unspent.
        if (e instanceof ApiError && e.code === 'reset_link') {
          navigate({ pathname: '/auth/reset-password', hash: `t=${token}` }, { replace: true });
          return;
        }
        setError(errorText(e));
        setCode(e instanceof ApiError ? e.code : null);
      });
  }, [navigate, token]);
  return (
    // No header here: the lockup and the line stand alone, centred on the page.
    <main className="shell pb-28 pt-8 sm:pt-12">
      <div className="mx-auto max-w-sm pt-20">
        <div className="mb-14" role="img" aria-label="Never Stop Building, build portal">
          <BrandLockup />
        </div>
        {renewed ? (
          <>
            <p className="text-lg">A new one is on its way to your email.</p>
            {renewed.devLink ? (
              <p className="mt-3 text-sm text-ink-3">
                Not sent from this machine:{' '}
                <a className="link" href={renewed.devLink}>
                  open it here
                </a>
              </p>
            ) : null}
          </>
        ) : code === 'expired' ? (
          <>
            <p className="text-lg">That link has run out.</p>
            <p className="mt-2 text-ink-2">We can send a new one to the address it came to.</p>
            <div className="mt-6">
              <Button busy={renewing} onClick={() => void renew()}>
                Send me a new one
              </Button>
            </div>
          </>
        ) : error ? (
          <>
            <p className="text-lg">{error}</p>
            <div className="mt-6">
              <Button onClick={() => navigate('/login')}>{code === 'staff_link' ? 'Sign in' : 'Get a new link'}</Button>
            </div>
          </>
        ) : (
          <p className="label animate-pulse">Signing you in</p>
        )}
      </div>
    </main>
  );
}
